<template>
                <div v-if="currentView === 'security_wizard'">
                    <div class="page-header"><div><div class="page-title">安全性設定精靈</div><div class="page-subtitle">設定 DKIM / SPF / DMARC 郵件驗證機制</div></div></div>

                    <div class="card p-6 mb-6">
                        <div class="flex gap-4 items-end mb-4">
                            <div class="flex-grow">
                                <label class="block text-sm font-bold mb-1">網域名稱</label>
                                <input v-model="securityDomain" type="text" class="input w-full"
                                    placeholder="example.com">
                            </div>
                            <div class="w-44">
                                <label class="block text-sm font-bold mb-1">DKIM Selector</label>
                                <input v-model="securitySelector" type="text" class="input w-full"
                                    placeholder="default">
                            </div>
                            <button @click="fetchSecurityStatus"
                                class="btn btn-primary mb-[1px]">檢查狀態</button>
                        </div>

                        <div class="grid grid-cols-1 md:grid-cols-5 gap-4 items-end mb-4">
                            <div>
                                <label class="block text-xs mb-1" style="color:var(--gray-600);">DMARC Policy</label>
                                <select v-model="securityDmarcPolicy" class="input">
                                    <option value="none">none (監控)</option>
                                    <option value="quarantine">quarantine</option>
                                    <option value="reject">reject</option>
                                </select>
                            </div>
                            <div class="md:col-span-2">
                                <label class="block text-xs mb-1" style="color:var(--gray-600);">RUA 收集信箱</label>
                                <input v-model="securityRuaEmail" type="text" class="input w-full"
                                    placeholder="留空則使用 admin@網域">
                            </div>
                            <div>
                                <label class="block text-xs mb-1" style="color:var(--gray-600);">PCT</label>
                                <input v-model.number="securityDmarcPct" type="number" min="0" max="100"
                                    class="input w-full">
                            </div>
                            <div class="flex gap-2">
                                <div class="flex-1">
                                    <label class="block text-xs mb-1" style="color:var(--gray-600);">adkim</label>
                                    <select v-model="securityDmarcAdkim" class="input">
                                        <option value="r">r</option>
                                        <option value="s">s</option>
                                    </select>
                                </div>
                                <div class="flex-1">
                                    <label class="block text-xs mb-1" style="color:var(--gray-600);">aspf</label>
                                    <select v-model="securityDmarcAspf" class="input">
                                        <option value="r">r</option>
                                        <option value="s">s</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div v-if="securityStatus">
                            <div v-if="securityStatus.exists" class="p-4 rounded mb-4" style="background:var(--color-success-bg);border:1px solid var(--color-success);">
                                <h3 class="font-bold" style="color:var(--color-success);">✅ 已配置 DKIM</h3>
                                <p class="text-sm" style="color:var(--gray-700);">選擇器 (Selector): {{ securityStatus.selector }}</p>
                            </div>
                            <div v-else class="p-4 rounded mb-4" style="background:var(--color-warning-bg);border:1px solid var(--color-warning);">
                                <h3 class="font-bold" style="color:var(--color-warning);">⚠️ 尚未配置 DKIM</h3>
                                <button @click="generateDkim"
                                    class="btn btn-warning mt-2">立即生成金鑰</button>
                            </div>
                        </div>

                        <div v-if="dnsSuggestions && dnsSuggestions.length > 0">
                            <h3 class="font-bold mb-2 mt-6" style="color:var(--gray-700);">建議的 DNS 記錄</h3>
                            <p class="text-sm mb-4" style="color:var(--gray-500);">請將以下記錄新增至您的 DNS 提供商 (如 Cloudflare, GoDaddy)。</p>

                            <div class="overflow-x-auto">
                                <table class="data-table">
                                    <thead>
                                        <tr style="background:var(--gray-100);">
                                            <th class="border p-2 w-20">類型</th>
                                            <th class="border p-2 w-32">主機名稱 (Host)</th>
                                            <th class="border p-2">值 (Value)</th>
                                            <th class="border p-2 w-44">說明</th>
                                            <th class="border p-2 w-20">操作</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(record, idx) in dnsSuggestions" :key="idx">
                                            <td class="border p-2 font-mono">{{ record.type }}</td>
                                            <td class="border p-2 font-mono">{{ record.host }}</td>
                                            <td class="border p-2 font-mono break-all text-xs">{{ record.value }}</td>
                                            <td class="border p-2 text-xs" style="color:var(--gray-600);">{{ record.desc || '—' }}</td>
                                            <td class="border p-2">
                                                <button @click="copyToClipboard(record.value)"
                                                    class="btn btn-ghost btn-sm text-xs" style="color:var(--brand-600);">複製值</button>
                                                <button @click="copyToClipboard(record.host)"
                                                    class="btn btn-ghost btn-sm text-xs" style="color:var(--brand-600);">複製 Host</button>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div class="mt-6">
                            <div class="flex items-center justify-between mb-2">
                                <h3 class="font-bold" style="color:var(--gray-700);">DNS 實際檢測 (公共 DNS)</h3>
                                <button @click="checkSecurityDns" :disabled="dnsCheckLoading"
                                    class="btn btn-dark btn-sm">
                                    {{ dnsCheckLoading ? '檢測中...' : '重新檢測' }}
                                </button>
                            </div>
                            <div v-if="dnsCheckResult" class="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div class="p-4 rounded border" style="background:var(--gray-50);">
                                    <div class="text-sm font-bold mb-1" style="color:var(--gray-600);">MX</div>
                                    <div class="text-sm" :style="dnsCheckResult.checks?.mx?.ok ? 'color:var(--color-success)' : 'color:var(--color-danger)'">
                                        {{ dnsCheckResult.checks?.mx?.ok ? '已偵測到 MX' : '未偵測到 MX' }}
                                    </div>
                                    <div v-if="(dnsCheckResult.checks?.mx?.all || []).length"
                                        class="mt-2 text-xs font-mono break-all" style="color:var(--gray-700);">
                                        {{ (dnsCheckResult.checks?.mx?.all || []).join(' | ') }}
                                    </div>
                                </div>
                                <div class="p-4 rounded border" style="background:var(--gray-50);">
                                    <div class="text-sm font-bold mb-1" style="color:var(--gray-600);">SPF</div>
                                    <div class="text-sm"
                                        :style="dnsCheckResult.checks?.spf?.ok ? 'color:var(--color-success)' : 'color:var(--color-danger)'">
                                        {{ dnsCheckResult.checks?.spf?.ok ? '已偵測到 v=spf1' : '未偵測到 SPF' }}
                                    </div>
                                    <div v-if="(dnsCheckResult.checks?.spf?.count || 0) > 1" class="text-xs mt-1" style="color:var(--color-warning);">
                                        偵測到多筆 SPF ({{ dnsCheckResult.checks?.spf?.count }})，可能導致驗證失敗
                                    </div>
                                    <div v-if="dnsCheckResult.checks?.spf?.found"
                                        class="mt-2 text-xs font-mono break-all" style="color:var(--gray-700);">
                                        {{ dnsCheckResult.checks?.spf?.found }}
                                    </div>
                                </div>
                                <div class="p-4 rounded border" style="background:var(--gray-50);">
                                    <div class="text-sm font-bold mb-1" style="color:var(--gray-600);">DMARC</div>
                                    <div class="text-sm"
                                        :style="dnsCheckResult.checks?.dmarc?.ok ? 'color:var(--color-success)' : 'color:var(--color-danger)'">
                                        {{ dnsCheckResult.checks?.dmarc?.ok ? '已偵測到 v=DMARC1' : '未偵測到 DMARC' }}
                                    </div>
                                    <div v-if="(dnsCheckResult.checks?.dmarc?.count || 0) > 1" class="text-xs mt-1" style="color:var(--color-warning);">
                                        偵測到多筆 DMARC ({{ dnsCheckResult.checks?.dmarc?.count }})，請保留單一 _dmarc TXT
                                    </div>
                                    <div v-if="dnsCheckResult.checks?.dmarc?.found"
                                        class="mt-2 text-xs font-mono break-all" style="color:var(--gray-700);">
                                        {{ dnsCheckResult.checks?.dmarc?.found }}
                                    </div>
                                </div>
                                <div class="p-4 rounded border md:col-span-2" style="background:var(--gray-50);">
                                    <div class="text-sm font-bold mb-1" style="color:var(--gray-600);">DKIM</div>
                                    <div class="text-sm"
                                        :style="dnsCheckResult.checks?.dkim?.ok ? 'color:var(--color-success)' : 'color:var(--color-danger)'">
                                        {{ dnsCheckResult.checks?.dkim?.ok ? '已偵測到 DKIM' : '未偵測到 DKIM' }}
                                    </div>
                                    <div v-if="dnsCheckResult.checks?.dkim?.match_expected === true"
                                        class="text-xs mt-1" style="color:var(--color-success);">與本機金鑰一致</div>
                                    <div v-else-if="dnsCheckResult.checks?.dkim?.match_expected === false"
                                        class="text-xs mt-1" style="color:var(--color-warning);">已偵測，但與本機金鑰不一致</div>
                                    <div v-if="dnsCheckResult.checks?.dkim?.found"
                                        class="mt-2 text-xs font-mono break-all" style="color:var(--gray-700);">
                                        {{ dnsCheckResult.checks?.dkim?.found }}
                                    </div>
                                </div>
                            </div>
                            <div v-if="dnsCheckResult && (dnsCheckResult.issues || []).length" class="mt-3 rounded p-3" style="background:var(--color-warning-bg);border:1px solid var(--color-warning);">
                                <div class="text-sm font-bold mb-1" style="color:var(--color-warning);">偵測到可能問題</div>
                                <div class="space-y-2">
                                    <div v-for="issue in (dnsCheckResult.issues || [])" :key="issue" class="rounded p-2" style="background:var(--surface);border:1px solid var(--color-warning);">
                                        <div class="text-sm font-bold" style="color:var(--color-warning);">{{ securityDnsIssueTitle(issue) }}</div>
                                        <div class="text-xs mt-1" style="color:var(--gray-700);">{{ securityDnsIssueDetail(issue) }}</div>
                                        <div v-if="issue === 'DKIM_DNS_MISMATCH'" class="flex flex-wrap gap-2 mt-2">
                                            <button v-if="dnsCheckResult.checks?.dkim?.expected" @click="copyToClipboard(dnsCheckResult.checks?.dkim?.expected)"
                                                class="btn btn-outline btn-xs">
                                                複製預期 DKIM
                                            </button>
                                            <button v-if="dnsCheckResult.checks?.dkim?.found" @click="copyToClipboard(dnsCheckResult.checks?.dkim?.found)"
                                                class="btn btn-outline btn-xs">
                                                複製目前 DKIM
                                            </button>
                                        </div>
                                        <div v-else-if="issue === 'DKIM_KEY_EXISTS_BUT_DNS_NOT_FOUND'" class="flex flex-wrap gap-2 mt-2">
                                            <button v-if="(dnsSuggestions || []).some(r => (r.host || '').includes('._domainkey'))"
                                                @click="copyToClipboard((dnsSuggestions || []).find(r => (r.host || '').includes('._domainkey'))?.host || '')"
                                                class="btn btn-outline btn-xs">
                                                複製 DKIM Host
                                            </button>
                                            <button v-if="(dnsSuggestions || []).some(r => (r.host || '').includes('._domainkey'))"
                                                @click="copyToClipboard((dnsSuggestions || []).find(r => (r.host || '').includes('._domainkey'))?.value || '')"
                                                class="btn btn-outline btn-xs">
                                                複製 DKIM 值
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div v-else class="text-sm" style="color:var(--gray-500);">
                                點擊「重新檢測」可確認 DNS 是否已生效 (DNS 可能需要一段時間傳播)。
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Edit Domain Modal -->
                <div v-if="editingDomain"
                    class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div class="card p-6 w-96">
                        <h3 class="card-title mb-4">編輯網域: {{ editingDomain.name }}</h3>
                        <div class="space-y-4">
                            <div>
                                <label class="block text-sm">最大用戶數 (0=不限)</label>
                                <input v-model="editDomainForm.max_users" type="number"
                                    class="input w-full">
                            </div>
                            <div>
                                <label class="block text-sm">最大別名數 (0=不限)</label>
                                <input v-model="editDomainForm.max_aliases" type="number"
                                    class="input w-full">
                            </div>
                            <div>
                                <label class="block text-sm">最大容量 (MB, 0=不限)</label>
                                <input v-model="editDomainForm.max_quota" type="number"
                                    class="input w-full">
                            </div>
                            <div>
                                <label class="block text-sm">稽核信箱</label>
                                <input v-model="editDomainForm.audit_email" type="email"
                                    class="input w-full">
                            </div>
                            <div class="flex items-center">
                                <input v-model="editDomainForm.active" type="checkbox" class="mr-2">
                                <label class="text-sm">啟用</label>
                            </div>
                            <div class="flex justify-end gap-2">
                                <button @click="editingDomain = null" class="btn btn-outline">
                                    取消
                                </button>
                                <button @click="updateDomain" class="btn btn-primary">
                                    儲存
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Users View -->

</template>

<script setup>
import { storeToRefs } from 'pinia'
import { useAppStore } from '../store/app.js'

const store = useAppStore()

// Spread all store state as reactive refs accessible in template
const stateRefs = storeToRefs(store)
// Destructure - Vue compiler exposes all variables declared in <script setup> to template
const {
  // Core state
  token, currentView, currentUser, loginForm, loginError,
  branding, notifications, showNotifPanel,
  // Command palette
  showCmdPalette, cmdQuery, cmdResults, cmdGrouped, cmdDefaults, cmdSelIdx,
  // Domain / Users / Departments
  domains, domainForm, showDomainForm, domainLoading,
  users, userForm, showUserForm, userLoading, userError,
  departments, deptForm, showDeptForm, deptLoading,
  // Contacts / Groups
  contacts, contactForm, showContactForm, contactLoading,
  groups, groupForm, showGroupForm, groupLoading,
  // Mail
  mailQueues, mailQueueLoading, mailQueueFilter,
  serviceStatus, serviceLoading,
  // Invoice Drive
  invoiceDriveState,
  // Quotes / Products
  quotes, quoteForm, showQuoteForm, quoteLoading,
  products, productForm, showProductForm, productLoading,
  priceBooks, priceBookForm, showPriceBookForm,
  // SSL
  sslCerts, sslLoading, sslDomain,
  // Security
  securityWizardStep, spamStats,
  securityFilterState,
  // Server settings
  serverMailSettings, serverDnsSettings, serverDbSettings, serverAiSettings,
  backupSettings,
  // Logs / Audit
  logs, logFilter, logLoading,
  mailAuditRecords, auditFilter, auditLoading,
  securityAuditLogs, securityAuditFilter,
  logAnalysisResult, logAnalysisLoading,
  // AI / RAG / Sales
  ragState, aiSalesState, aiMarketingState,
  // LINE
  lineSupportState,
  // Webmail
  webmailState, webmailAutoScanInvoices, webmailAutoScanMarkRead,
  // Unreplied
  unrepliedEmails, unrepliedLoading,
  // CBI
  cbiInsights, cbiLoading,
  // Misc
  sidebarCollapsed, sidebarMobile,
  journeyEnrollments, journeyEnrollmentsLoading,
  journeyFunnel, journeyFunnelLoading, journeyFunnelFilters,
  showJourneyEditorModal, showJourneyFunnelModal, showJourneyEnrollmentsModal,
  selectedJourneyForEnrollments, selectedJourneyForFunnel,
  journeyEditorForm,
  securityDomain, securitySelector, securityDmarcPolicy, securityRuaEmail,
  securityDmarcPct, securityDmarcAdkim, securityDmarcAspf,
  securityStatus, dnsSuggestions, dnsCheckResult, dnsCheckLoading,
} = stateRefs

// Methods are accessed directly from store (not reactive, no storeToRefs needed)
const {
  login, logout, switchSidebarView, canAccess,
  openCommandPalette, closeCmdPalette, updateCmdResults, execCmdItemById,
  moveCmdSel, toggleNotifPanel, markNotifRead, clearNotifs,
  fetchDomains, saveDomain, deleteDomain,
  fetchUsers, saveUser, deleteUser, resetUserPassword,
  fetchDepartments, saveDepartment, deleteDepartment,
  fetchContacts, saveContact, deleteContact, importContacts,
  fetchGroups, saveGroup, deleteGroup,
  fetchMailQueue, releaseMailQueueItem, deleteMailQueueItem, flushMailQueue,
  fetchServiceStatus, fetchSpamStats,
  fetchSslCerts, issueSslCert, deleteSslCert,
  fetchProducts, saveProduct, deleteProduct,
  fetchPriceBooks, savePriceBook, deletePriceBook,
  fetchQuotes, saveQuote, deleteQuote, sendQuote,
  fetchLogs, fetchLogAnalysis,
  fetchMailAuditRecords, fetchSecurityAuditLogs,
  fetchServerMailSettings, saveServerMailSettings,
  fetchServerDnsSettings, saveServerDnsSettings,
  fetchServerDbSettings, saveServerDbSettings,
  fetchServerAiSettings, saveServerAiSettings,
  fetchBackupSettings, saveBackupSettings, triggerBackup,
  fetchSecurityFilter, saveSecurityFilter,
  fetchRagDocuments, uploadRagDocument, deleteRagDocument, queryRag,
  fetchUnreplied,
  fetchCbiInsights,
  loadBrandingFromLocal, saveBranding,
  safeArr, keyById,
  invoiceDriveCanGotoAdjacent, invoiceDriveGotoAdjacent,
  openInvoiceDetail, closeInvoiceDetail,
  fetchSecurityStatus, generateDkim, checkSecurityDns, copyToClipboard,
} = store
</script>
